import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import { ActivateScheduleDialog } from '../components/activate-schedule-dialog';
import { cadenceEvery } from '../components/cadence-text';
import { DeleteScheduleDialog } from '../components/delete-schedule-dialog';
import { EndScheduleDialog } from '../components/end-schedule-dialog';
import { NotAddedAlert } from '../components/not-added-alert';
import { PoolTab } from '../components/pool-tab';
import { PreviewDialog } from '../components/preview-dialog';
import { RunsTab } from '../components/runs-tab';
import { ScheduleActions, type ScheduleDialog } from '../components/schedule-actions';
import { ScheduleOverview } from '../components/schedule-overview';
import { ScheduleStatusBadge } from '../components/schedule-status-badge';
import { SettingsTab } from '../components/settings-tab';
import { UpcomingTab } from '../components/upcoming-tab';
import { useSchedule } from '../hooks/use-schedule';
import {
  SCHEDULE_PAGE_TABS,
  schedulePageTab,
  type SchedulePageTab,
} from '../schemas/schedule-page-search';
import { isWritable } from '../schemas/schedule-rules';

const ROUTE_ID = '/_authed/_active/org/schedules/$scheduleId' as const;
const ROUTE_PATH = '/org/schedules/$scheduleId' as const;

const isTab = (value: string): value is SchedulePageTab =>
  (SCHEDULE_PAGE_TABS as readonly string[]).includes(value);

function ScheduleDetail() {
  const { t } = useTranslation('schedules');
  const { scheduleId } = useParams({ from: ROUTE_ID });
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const { can } = usePermissions();
  // Prefetched by the route loader; polls while a run is in progress.
  const query = useSchedule(scheduleId);
  const [dialog, setDialog] = useState<ScheduleDialog | null>(null);
  const schedule = query.data;
  if (!schedule) {
    return query.error ? (
      <PageContent>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </PageContent>
    ) : null;
  }

  const canWrite = can('products.write');
  const tab = schedulePageTab(search);
  const openRun = (runId: string) =>
    void navigate({ search: (prev) => ({ ...prev, tab: 'runs', run: runId }) });
  const previewOpen = dialog === 'preview' || (search.preview === true && isWritable(schedule));
  const closeDialog = () => {
    setDialog(null);
    if (search.preview)
      void navigate({ search: (prev) => ({ ...prev, preview: undefined }), replace: true });
  };

  return (
    <PageContent>
      <PageHeader
        title={schedule.name}
        meta={<ScheduleStatusBadge status={schedule.status} />}
        description={t('detail.description', {
          count: schedule.liveCount,
          every: cadenceEvery(t, schedule),
          zone: schedule.timeZone,
          stores: t('detail.stores', { count: schedule.stores.length }),
        })}
        backTo={{ to: '/org/schedules' }}
        backLabel={t('title')}
        tour="schedules.detail.header"
        actions={
          <ScheduleActions
            schedule={schedule}
            canWrite={canWrite}
            onOpen={setDialog}
            onRunQueued={openRun}
          />
        }
      />
      <NotAddedAlert scheduleId={schedule.id} stores={schedule.stores} />
      <Tabs
        value={tab}
        onValueChange={(value) => {
          if (isTab(value)) {
            void navigate({
              search: (prev) => ({
                ...prev,
                tab: value,
                ...(value === 'runs' ? {} : { run: undefined }),
              }),
            });
          }
        }}
      >
        <TabsList variant="line" className="mb-2" data-tour="schedules.detail.tabs">
          <TabsTrigger value="overview">{t('tabs.overview')}</TabsTrigger>
          <TabsTrigger value="pool" data-tour="schedules.detail.pool-tab">
            {t('tabs.pool', { count: schedule.members.total })}
          </TabsTrigger>
          <TabsTrigger value="runs" data-tour="schedules.detail.runs-tab">
            {t('tabs.runs')}
          </TabsTrigger>
          <TabsTrigger value="upcoming" data-tour="schedules.detail.upcoming-tab">
            {t('tabs.upcoming')}
          </TabsTrigger>
          <TabsTrigger value="settings" data-tour="schedules.detail.settings-tab">
            {t('tabs.settings')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <ScheduleOverview schedule={schedule} />
        </TabsContent>
        <TabsContent value="pool">
          <PoolTab
            schedule={schedule}
            canWrite={canWrite && isWritable(schedule)}
            onOpenRun={openRun}
          />
        </TabsContent>
        <TabsContent value="runs">
          <RunsTab schedule={schedule} />
        </TabsContent>
        <TabsContent value="upcoming">
          <UpcomingTab schedule={schedule} />
        </TabsContent>
        {/* Kept mounted so switching tabs never drops unsaved settings. */}
        <TabsContent value="settings" forceMount className="data-[state=inactive]:hidden">
          <SettingsTab schedule={schedule} canWrite={canWrite} onReleased={openRun} />
        </TabsContent>
      </Tabs>
      <PreviewDialog
        schedule={schedule}
        open={previewOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        canWrite={canWrite}
      />
      {canWrite ? (
        <>
          <ActivateScheduleDialog
            schedule={schedule}
            open={dialog === 'activate'}
            onOpenChange={(open) => {
              if (!open) closeDialog();
            }}
          />
          <EndScheduleDialog
            key={`end-${schedule.onEnd}`}
            schedule={schedule}
            open={dialog === 'end'}
            onOpenChange={(open) => {
              if (!open) closeDialog();
            }}
            onEnded={(releaseRunId) => {
              if (releaseRunId) openRun(releaseRunId);
            }}
          />
          <DeleteScheduleDialog
            schedule={schedule}
            open={dialog === 'delete'}
            onOpenChange={(open) => {
              if (!open) closeDialog();
            }}
            onDeleted={() => {
              releaseSaveBar();
              void navigate({ to: '/org/schedules' });
            }}
          />
        </>
      ) : null}
    </PageContent>
  );
}

export function ScheduleDetailPage() {
  return (
    <FeatureErrorBoundary name="schedules.detail">
      <ScheduleDetail />
    </FeatureErrorBoundary>
  );
}
